import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

// Module-level: tracks recently-copied snapshots so the "Copiado" label
// survives the constant remounts the markdown parser triggers during
// streaming. We key by an EXACT text snapshot taken at click time and
// then ask "is the current code text still a superset of this snapshot?".
// That handles the case where the user copies while the block is still
// growing — the snapshot remains a prefix of all subsequent texts.
const COPIED_TTL_MS = 2000;
const copiedSnapshots = new Map<string, number>(); // `${lang}::${text}` → expiry
const copyListeners = new Set<() => void>();

function pruneExpired() {
  const now = Date.now();
  let changed = false;
  for (const [k, v] of copiedSnapshots) {
    if (v <= now) {
      copiedSnapshots.delete(k);
      changed = true;
    }
  }
  if (changed) copyListeners.forEach((fn) => fn());
}

function notifyCopy(lang: string, text: string) {
  copiedSnapshots.set(`${lang}::${text}`, Date.now() + COPIED_TTL_MS);
  copyListeners.forEach((fn) => fn());
  setTimeout(pruneExpired, COPIED_TTL_MS + 50);
}

function useCopiedFlag(text: string, lang: string): boolean {
  const [, setTick] = useState(0);
  useEffect(() => {
    const handler = () => setTick((x) => x + 1);
    copyListeners.add(handler);
    return () => {
      copyListeners.delete(handler);
    };
  }, []);
  const now = Date.now();
  const prefix = `${lang}::`;
  for (const [key, until] of copiedSnapshots) {
    if (until <= now) continue;
    if (!key.startsWith(prefix)) continue;
    const snapshotText = key.slice(prefix.length);
    if (snapshotText.length > 0 && text.startsWith(snapshotText)) return true;
  }
  return false;
}

const TickIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    stroke='currentColor'
    fill='none'
    strokeWidth='2'
    viewBox='0 0 24 24'
    strokeLinecap='round'
    strokeLinejoin='round'
    height='14'
    width='14'
    xmlns='http://www.w3.org/2000/svg'
    {...props}
  >
    <polyline points='20 6 9 17 4 12'></polyline>
  </svg>
);

const CopyIcon = () => (
  <svg
    stroke='currentColor'
    fill='none'
    strokeWidth='2'
    viewBox='0 0 24 24'
    strokeLinecap='round'
    strokeLinejoin='round'
    height='14'
    width='14'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path d='M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2'></path>
    <rect x='8' y='2' width='8' height='4' rx='1' ry='1'></rect>
  </svg>
);

function stringifyChildren(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(stringifyChildren).join('');
  if (React.isValidElement(children)) {
    const props = children.props as { children?: React.ReactNode };
    return stringifyChildren(props.children);
  }
  return '';
}

const CodeBlock = ({
  lang,
  codeChildren,
}: {
  lang: string;
  codeChildren: React.ReactNode & React.ReactNode[];
}) => {
  const text = stringifyChildren(codeChildren);

  return (
    <CodeBlockArea>
      <CodeBar lang={lang} text={text} />
      <CodeBlockBody>
        <pre style={{ margin: 0 }}>
          <StyledCode className={`!whitespace-pre hljs language-${lang}`}>
            {codeChildren}
          </StyledCode>
        </pre>
      </CodeBlockBody>
    </CodeBlockArea>
  );
};

const CodeBar = React.memo(
  ({
    lang,
    text,
  }: {
    lang: string;
    text: string;
  }) => {
    const isCopied = useCopiedFlag(text, lang);
    return (
      <CodeBarHeader>
        <LangLabel>{lang}</LangLabel>
        <CopyButton
          aria-label='copiar bloco de código'
          onClick={async () => {
            if (!text) return;
            try {
              await navigator.clipboard.writeText(text);
              notifyCopy(lang, text);
            } catch {
              /* clipboard unavailable */
            }
          }}
        >
          {isCopied ? (
            <>
              <TickIcon />
              <span>Copiado</span>
            </>
          ) : (
            <>
              <CopyIcon />
              <span>Copiar</span>
            </>
          )}
        </CopyButton>
      </CodeBarHeader>
    );
  }
);
export default CodeBlock;

const CodeBlockArea = styled.div`
  margin: 10px 0;
  border-radius: 10px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)'};
  overflow: hidden;
  font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', Consolas, Monaco, monospace;
`;

const CodeBarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px 6px 12px;
  font-size: 0.72rem;
  font-family: 'Sora', 'Lexend', sans-serif;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.5)'};
  border-bottom: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
  background: transparent;
`;

const LangLabel = styled.span`
  text-transform: lowercase;
  letter-spacing: 0.02em;
  opacity: 0.85;
`;

const CodeBlockBody = styled.div`
  padding: 12px 14px;
  overflow-x: auto;
  font-size: 0.85rem;
  line-height: 1.55;
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  border: none;
  cursor: pointer;
  font-family: 'Sora', 'Lexend', sans-serif;
  font-size: 0.72rem;
  transition: background 120ms ease, color 120ms ease;
  min-width: 82px;
  & span { line-height: 1; }
  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
    color: ${({ theme }) => theme.font_color || '#222'};
  }
`;

const StyledCode = styled.code`
  white-space: pre !important;
  color: ${({ theme }) => theme.font_color || '#222'};
  background: transparent !important;
  padding: 0 !important;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
`;
